import { useMemo, useState } from 'react'
import {
    Modal as ServaModal,
	Row,
	Tag,
	Tooltip,
	Text,
} from '@servahealth/serva-ui-kit';
import {
	Col,
} from 'antd';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { CoreType, CoreDataPoint, CoreDataPointDataType} from './CoreComponents';
import { AHRBCoreDataPointCustomization } from './AHRBCoreDataPointCustomizations';
import { AHRBCoreDataPointAggregate } from './AHRBCoreDataPointAggregate';
import { AHRBCoreDataPointFixedWhere } from './AHRBCoreDataPointFixedWhere';
import { AHRBCoreDataPointVariableWhere } from './AHRBCoreDataPointVariableWhere';
import DataSetColumnFormatModel from '../../Interfaces/Responses/Reports/DataSets/DatasetColumnFormatModel';
import AdHocReportVariableWhereModel from '../../Interfaces/Requests/Reports/AdHocReportVariableWhereModel';
import { AHRBDataPointCustomizationMap,
		 AHRBDataPointAggregateMap,
		 AHRBDataPointFixedWhereMap,
		 AHRBDataPointAggregateConfiguration,
		 AHRBDataPointFixedWhereConfiguration} from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';

type AHRBSDataPointsProps = {
	coreTypes: CoreType[];
	selectedCoreTypeIDs: number[];
	selectedCoreTypes: CoreType[];
	selectedCoreDataPoints: CoreDataPoint[];
	brandID: number;
	selectedOrderByDataPointID: number | undefined;
	dataPointCustomizations: AHRBDataPointCustomizationMap;
	dataPointAggregates: AHRBDataPointAggregateMap;
	dataPointFixedWheres: AHRBDataPointFixedWhereMap;
	dataPointVariableWheres: AdHocReportVariableWhereModel[];
	handleSetSelectedOrderByDataPointID: (
		selectedOrderByDataPointID: number | undefined
	) => void;
	handleSetDataPointCustomizations: (
		dataPointCustomizations: AHRBDataPointCustomizationMap
	) => void;
	handleSetDataPointAggregates: (
		dataPointAggregates: AHRBDataPointAggregateMap
	) => void;
	handleSetDataPointFixedWheres: (
		dataPointFixedWheres: AHRBDataPointFixedWhereMap
	) => void;
	handleSetDataPointVariableWheres: (
		dataPointVariableWheres: AdHocReportVariableWhereModel[]
	) => void;
};

export const AHRBSDataPoints: React.FC<AHRBSDataPointsProps> = ({ ...props }) => {

	const createDefaultDataSetColumnFormatModel = (): DataSetColumnFormatModel => ({
		id: 0,
		columnName: '',
		dataFormat: '',
		format: '',
		formatExcel: null,
		aggregate: '',
		columnTitle: '',
		includeInGrandTotal: false,
		allowFiltering: false,
		hide: false,
		bgColor: null,
		foreColor: null,
		alignment: '',
		grouping: false,
		toolTip: null,
		widthExcel: null,
		allowHtml: false,
		jsDataType: 'string'
	});		

	const [selectedCustomizationDataPointID, setSelectedCustomizationDataPointID] = useState<number | undefined>();
	const [selectedAggregateDataPointID, setSelectedAggregateDataPointID] = useState<number | undefined>();
	const [selectedFixedWhereDataPointID, setSelectedFixedWhereDataPointID] = useState<number | undefined>();
	const [selectedVariableWhereDataPointID, setSelectedVariableWhereDataPointID] = useState<number | undefined>();

	const [selectedCustomizationDataPoint, setSelectedCustomizationDataPoint] = useState<DataSetColumnFormatModel>(createDefaultDataSetColumnFormatModel());
	const [selectedAggregateDataPoint, setSelectedAggregateDataPoint] = useState<AHRBDataPointAggregateConfiguration | undefined>();
	const [selectedFixedWhereDataPoint, setSelectedFixedWhereDataPoint] = useState<AHRBDataPointFixedWhereConfiguration | undefined>();
	const [selectedVariableWhereDataPoint, setSelectedVariableWhereDataPoint] = useState<AdHocReportVariableWhereModel | undefined>();

	const [ahrbReportCoreDataPointCustomizationOpen, setAHRBReportCoreDataPointCustomizationOpen] = useState(false);
	const [ahrbReportCoreDataPointAggregateOpen, setAHRBReportCoreDataPointAggregateOpen] = useState(false);
	const [ahrbReportCoreDataPointFixedWhereOpen, setAHRBReportCoreDataPointFixedWhereOpen] = useState(false);
	const [ahrbReportCoreDataPointVariableWhereOpen, setAHRBReportCoreDataPointVariableWhereOpen] = useState(false);

	const flattenedDataPoints = useMemo(() => {
		return props.selectedCoreDataPoints.map(dataPoint => {
			const coreType = props.coreTypes.find(x => x.id === dataPoint.idType);

			return {
				coreTypeID: dataPoint.idType,
				coreTypeName: coreType?.name ?? '',
				dataPoint
			};
		});
	}, [props.coreTypes, props.selectedCoreDataPoints]);								

	const normalizeCoreDataPointDataType = (dataType: CoreDataPointDataType | string | number | undefined): CoreDataPointDataType | undefined => {
		if (dataType === undefined || dataType === null) {
			return undefined;
		}

		if (typeof dataType === 'number') {
			return dataType as CoreDataPointDataType;
		}

		if (typeof dataType === 'string') {
			const normalizedDataType = dataType.toUpperCase();

			if (normalizedDataType === 'INTEGER' || normalizedDataType === '1') {
				return CoreDataPointDataType.Integer;
			}
			else if (normalizedDataType === 'FLOAT' || normalizedDataType === '2') {
				return CoreDataPointDataType.Float;
			}
			else if (normalizedDataType === 'STRING' || normalizedDataType === '3') {
				return CoreDataPointDataType.String;
			}
			else if (normalizedDataType === 'DATETIME' || normalizedDataType === '4') {
				return CoreDataPointDataType.DateTime;
			}
			else if (normalizedDataType === 'BINARY' || normalizedDataType === '5') {
				return CoreDataPointDataType.Binary;
			}
			else if (normalizedDataType === 'LOOKUP' || normalizedDataType === '6') {
				return CoreDataPointDataType.Lookup;
			}
			else if (normalizedDataType === 'NOTUSED' || normalizedDataType === '7') {
				return CoreDataPointDataType.NOTUSED;
			}
			else if (normalizedDataType === 'AUTONUMBER' || normalizedDataType === '8') {
				return CoreDataPointDataType.Autonumber;
			}
			else if (normalizedDataType === 'PHONENUMBER' || normalizedDataType === '9') {
				return CoreDataPointDataType.Phonenumber;
			}
			else if (normalizedDataType === 'DATE' || normalizedDataType === '10') {
				return CoreDataPointDataType.Date;
			}
			else if (normalizedDataType === 'TENANTTRACKABLE' || normalizedDataType === '11') {
				return CoreDataPointDataType.TenantTrackable;
			}
		}

		return undefined;
	};

		const getCoreDataPointDataTypeDisplayText = (
			dataType: CoreDataPointDataType | string | number | undefined
		): string => {
			const normalizedDataType = normalizeCoreDataPointDataType(dataType);

			switch (normalizedDataType) {
				case CoreDataPointDataType.Integer:
					return 'Integer';

				case CoreDataPointDataType.Float:
					return 'Float';

				case CoreDataPointDataType.String:
					return 'String';

				case CoreDataPointDataType.DateTime:
					return 'DateTime';

				case CoreDataPointDataType.Binary:
					return 'Binary';

				case CoreDataPointDataType.Lookup:
					return 'Lookup';

				case CoreDataPointDataType.NOTUSED:
					return 'Not Used';

				case CoreDataPointDataType.Autonumber:
					return 'Autonumber';

				case CoreDataPointDataType.Phonenumber:
					return 'Phone Number';

				case CoreDataPointDataType.Date:
					return 'Date';

				case CoreDataPointDataType.TenantTrackable:
					return 'Tenant Trackable';

				default:
					return dataType?.toString() ?? '';
			}
		};

	const mapCoreDataPointDataTypeToJSDataType = (dataType: CoreDataPointDataType | string | number | undefined): 'string' | 'number' | 'date' | null => {
	    const normalizedDataType = normalizeCoreDataPointDataType(dataType);

    	switch (normalizedDataType) {
			case CoreDataPointDataType.Integer:
			case CoreDataPointDataType.Float:
			case CoreDataPointDataType.Autonumber:
				return 'number';

			case CoreDataPointDataType.Date:
			case CoreDataPointDataType.DateTime:
				return 'date';

			case CoreDataPointDataType.String:
			case CoreDataPointDataType.Phonenumber:
				return 'string';

			case CoreDataPointDataType.Binary:
			case CoreDataPointDataType.Lookup:
			case CoreDataPointDataType.NOTUSED:
			case CoreDataPointDataType.TenantTrackable:
				return null;

			default:
				return null;
		}
	};

	const dataPointSupportsFixedWhere = (dataType: CoreDataPointDataType | string | number | undefined): boolean => {
		const normalizedDataType = normalizeCoreDataPointDataType(dataType);

		switch (normalizedDataType) {
			case CoreDataPointDataType.Integer:
			case CoreDataPointDataType.Float:
			case CoreDataPointDataType.Autonumber:
			case CoreDataPointDataType.Date:
			case CoreDataPointDataType.DateTime:
			case CoreDataPointDataType.String:
			case CoreDataPointDataType.Phonenumber:
			case CoreDataPointDataType.Lookup:
				return true;

			default:
				return false;
		}
	};

	const dataPointSupportsVariableWhere = (dataType: CoreDataPointDataType | string | number | undefined): boolean => {
		const normalizedDataType = normalizeCoreDataPointDataType(dataType);

		switch (normalizedDataType) {
			case CoreDataPointDataType.Integer:
			case CoreDataPointDataType.Float:
			case CoreDataPointDataType.Autonumber:
			case CoreDataPointDataType.Date:
			case CoreDataPointDataType.DateTime:
			case CoreDataPointDataType.String:
			case CoreDataPointDataType.Phonenumber:
			case CoreDataPointDataType.Lookup:
				return true;

			default:
				return false;
		}
	};

	const dataPointSupportsAggregate = (dataType: CoreDataPointDataType | string | number | undefined): boolean => {
		const normalizedDataType = normalizeCoreDataPointDataType(dataType);

		switch (normalizedDataType) {
			case CoreDataPointDataType.Integer:
			case CoreDataPointDataType.Float:
			case CoreDataPointDataType.Autonumber:
			case CoreDataPointDataType.Date:
			case CoreDataPointDataType.DateTime:
			case CoreDataPointDataType.String:
			case CoreDataPointDataType.Phonenumber:
			case CoreDataPointDataType.Lookup:
				return true;

			default:
				return false;
		}
	};

	const handleFormatButtonClick = (dataPointID: number) => {

		const selectedDataPoint = props.selectedCoreDataPoints.find(dp => dp.id === dataPointID);

		const jsDataType = mapCoreDataPointDataTypeToJSDataType(selectedDataPoint?.dataType);

		if (jsDataType === null) {
			return;
		}

		const existingCustomization = props.dataPointCustomizations[dataPointID];

		setSelectedCustomizationDataPoint(
			existingCustomization ?? {
				...createDefaultDataSetColumnFormatModel(),
				columnName: selectedDataPoint?.name ?? '',
				jsDataType: jsDataType
			}
		);

		setSelectedCustomizationDataPointID(dataPointID);

		setAHRBReportCoreDataPointCustomizationOpen(true);
	};

	const handleFixedWhereButtonClick = (dataPointID: number) => {

		setSelectedFixedWhereDataPoint(props.dataPointFixedWheres[dataPointID]);

		setSelectedFixedWhereDataPointID(dataPointID);

		setAHRBReportCoreDataPointFixedWhereOpen(true);
	};

	const handleVariableWhereButtonClick = (dataPointID: number) => {

		setSelectedVariableWhereDataPoint(props.dataPointVariableWheres[dataPointID]);

		setSelectedVariableWhereDataPointID(dataPointID);

		setAHRBReportCoreDataPointVariableWhereOpen(true);
	};

	const handleAggregateButtonClick = (dataPointID: number) => {

		setSelectedAggregateDataPoint(props.dataPointAggregates[dataPointID]);

		setSelectedAggregateDataPointID(dataPointID);

		setAHRBReportCoreDataPointAggregateOpen(true);
	};

	const handleOrderByTagClick = (dataPointID: number) => {
		const newSelectedOrderByDataPointID =
			props.selectedOrderByDataPointID === dataPointID
				? undefined
				: dataPointID;

		props.handleSetSelectedOrderByDataPointID(newSelectedOrderByDataPointID);
	};

	/*const hasDataPointCustomization = (dataPointFormat: DataSetColumnFormatModel): boolean => {
		const alignmentChanged = dataPointFormat.alignment.trim().length > 0;

		const allowHtmlChanged = dataPointFormat.allowHtml === true;

		const columnTitleChanged = dataPointFormat.columnTitle.trim().length > 0 &&
								   dataPointFormat.columnTitle !== dataPointFormat.columnName;

		const formatChanged = dataPointFormat.format.trim().length > 0;

		const hideChanged = dataPointFormat.hide === true;

		const jsDataTypeChanged = dataPointFormat.jsDataType !== 'string';

		return (
			alignmentChanged ||
			allowHtmlChanged ||
			columnTitleChanged ||
			formatChanged ||
			hideChanged ||
			jsDataTypeChanged
		);
	};*/

	const hasDataPointCustomization = (dataPointFormat: DataSetColumnFormatModel, originalDataPointFormat: DataSetColumnFormatModel): boolean => {
		const alignmentChanged =
			dataPointFormat.alignment !== originalDataPointFormat.alignment;

		const allowHtmlChanged =
			dataPointFormat.allowHtml !== originalDataPointFormat.allowHtml;

		const columnTitleChanged =
			dataPointFormat.columnTitle.trim().length > 0 &&
			dataPointFormat.columnTitle !== originalDataPointFormat.columnName;

		const formatChanged =
			dataPointFormat.format !== originalDataPointFormat.format;

		const hideChanged =
			dataPointFormat.hide !== originalDataPointFormat.hide;

		const jsDataTypeChanged =
			dataPointFormat.jsDataType !== originalDataPointFormat.jsDataType;

		return (
			alignmentChanged ||
			allowHtmlChanged ||
			columnTitleChanged ||
			formatChanged ||
			hideChanged ||
			jsDataTypeChanged
		);
	};	

	/*const handleSetCoreDataPointCustomization = (dataPointFormat: DataSetColumnFormatModel) => {
		if (selectedCustomizationDataPointID === undefined) {
			return;
		}

		if (!hasDataPointCustomization(dataPointFormat)) {
			const updatedDataPointCustomizations = { ...props.dataPointCustomizations };

			delete updatedDataPointCustomizations[selectedCustomizationDataPointID];

			props.handleSetDataPointCustomizations(updatedDataPointCustomizations);

			setAHRBReportCoreDataPointCustomizationOpen(false);

			return;
		}

		props.handleSetDataPointCustomizations({
			...props.dataPointCustomizations,
			[selectedCustomizationDataPointID]: dataPointFormat
		});

		setAHRBReportCoreDataPointCustomizationOpen(false);
	};*/

	const handleSetCoreDataPointCustomization = (dataPointFormat: DataSetColumnFormatModel) => {
		if (selectedCustomizationDataPointID === undefined) {
			return;
		}

		const selectedDataPoint = props.selectedCoreDataPoints.find(
			dataPoint => dataPoint.id === selectedCustomizationDataPointID
		);

		const originalJSDataType = mapCoreDataPointDataTypeToJSDataType(selectedDataPoint?.dataType);

		const originalDataPointFormat: DataSetColumnFormatModel = {
			...createDefaultDataSetColumnFormatModel(),
			columnName: selectedDataPoint?.name ?? '',
			jsDataType: originalJSDataType ?? 'string'
		};

		if (!hasDataPointCustomization(dataPointFormat, originalDataPointFormat)) {
			const updatedDataPointCustomizations = { ...props.dataPointCustomizations };

			delete updatedDataPointCustomizations[selectedCustomizationDataPointID];

			props.handleSetDataPointCustomizations(updatedDataPointCustomizations);

			setAHRBReportCoreDataPointCustomizationOpen(false);

			return;
		}

		props.handleSetDataPointCustomizations({
			...props.dataPointCustomizations,
			[selectedCustomizationDataPointID]: dataPointFormat
		});

		setAHRBReportCoreDataPointCustomizationOpen(false);
	};	

	const handleSetCoreDataPointFixedWhere = (dataPointFixedWhere: AHRBDataPointFixedWhereConfiguration) => {
		if (selectedFixedWhereDataPointID === undefined) {
			return;
		}

		if (dataPointFixedWhere.where.trim().length === 0) {
			const updatedDataPointFixedWheres = { ...props.dataPointFixedWheres };

			delete updatedDataPointFixedWheres[selectedFixedWhereDataPointID];

			props.handleSetDataPointFixedWheres(updatedDataPointFixedWheres);

			setAHRBReportCoreDataPointFixedWhereOpen(false);

			return;
		}

		props.handleSetDataPointFixedWheres({
			...props.dataPointFixedWheres,
			[selectedFixedWhereDataPointID]: dataPointFixedWhere
		});

		setAHRBReportCoreDataPointFixedWhereOpen(false);
	};

	const handleSetCoreDataPointVariableWhere = (dataPointVariableWhere: AdHocReportVariableWhereModel) => {
		const updatedDataPointVariableWheres = [...props.dataPointVariableWheres];

		if (selectedVariableWhereDataPointID === undefined) {
			return;
		}

		if (dataPointVariableWhere.where.trim().length === 0) {
			delete updatedDataPointVariableWheres[selectedVariableWhereDataPointID];

			props.handleSetDataPointVariableWheres(updatedDataPointVariableWheres);

			setAHRBReportCoreDataPointVariableWhereOpen(false);

			return;
		}

		updatedDataPointVariableWheres[selectedVariableWhereDataPointID] = dataPointVariableWhere;

		props.handleSetDataPointVariableWheres(updatedDataPointVariableWheres);

		setAHRBReportCoreDataPointVariableWhereOpen(false);
	};

	const handleSetCoreDataPointAggregate = (dataPointAggregate: AHRBDataPointAggregateConfiguration) => {
		if (selectedAggregateDataPointID === undefined) {
			return;
		}

		if (dataPointAggregate.aggregate.trim().length === 0) {
			const updatedDataPointAggregates = { ...props.dataPointAggregates };

			delete updatedDataPointAggregates[selectedAggregateDataPointID];

			props.handleSetDataPointAggregates(updatedDataPointAggregates);

			setAHRBReportCoreDataPointAggregateOpen(false);

			return;
		}

		props.handleSetDataPointAggregates({
			[selectedAggregateDataPointID]: dataPointAggregate
		});

		setAHRBReportCoreDataPointAggregateOpen(false);
	};

	const selectedDataPointRows = flattenedDataPoints.map(({ dataPoint, coreTypeName }, globalIndex) => {
		const selectedFieldLabel = `[${coreTypeName}]: ${dataPoint.name}`;

		return (
			<Draggable
				key={dataPoint.id}
				draggableId={dataPoint.id.toString()}
				index={globalIndex}
			>
				{(provided, snapshot) => (
					<div
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						style={{
							background: snapshot.isDragging ? 'lightgray' : 'white',
							border: snapshot.isDragging ? '2px solid blue' : '2px solid lightgray',
							borderRadius: '5px',
							color: 'black',
							marginBottom: '5px',
							padding: '5px',
							userSelect: "none",
							width: "100%",
							...provided.draggableProps.style
						}}
					>
						<Row style={{ textAlign: 'left' }}>
							<Col span={12} style={{ textAlign: 'left' }}>
								<Tooltip title={selectedFieldLabel}>
									<span
										style={{
											display: 'block',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											width: '100%'
										}}
									>
										{selectedFieldLabel}
									</span>
								</Tooltip>
							</Col>
							<Col span={12} style={{ textAlign: 'right' }}>
								<span
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: '4px',
										whiteSpace: 'nowrap'
									}}
								>
									{
										dataPoint.isPHI
											? (
												<Tooltip title='This field contains Protected Health Information'>
													<Tag color='red'>PHI</Tag>
												</Tooltip>
											)
											: null
									}
									<span style={{ fontSize: '10px' }}>
										{getCoreDataPointDataTypeDisplayText(dataPoint.dataType)}
									</span>
								</span>
							</Col>
						</Row>

						<Row>
							<Col span={24}>
								<hr style={{ marginTop: '2px', marginBottom: '2px' }} />
							</Col>
						</Row>

						<Row justify='end'>
							<span>
								<Tag
									color={props.dataPointCustomizations[dataPoint.id] !== undefined ? 'green' : 'blue'}
									style={{ paddingLeft: '2px', paddingRight: '2px', marginRight: '2px', marginLeft: '2px', cursor: 'pointer', userSelect: 'none' }}
									onClick={() => handleFormatButtonClick(dataPoint.id)}
								>
									Format ⚙
								</Tag>

								{(() => {
									const supportsVariableWhere = dataPointSupportsVariableWhere(dataPoint.dataType);

									return (
										<Tag
											color={
												(() => {
													const variableWhere = props.dataPointVariableWheres[dataPoint.id];

													if (!variableWhere || variableWhere.where.trim().length === 0) {
														return 'blue';
													}

													if (
														variableWhere.where === 'IsNull' ||
														variableWhere.where === 'IsNotNull'
													) {
														return 'green';
													}

													if (variableWhere.where === 'Between') {
														return variableWhere.valueLHS.trim().length > 0 && variableWhere.valueRHS.trim().length > 0
															? 'green'
															: 'blue';
													}

													return variableWhere.valueLHS.trim().length > 0
														? 'green'
														: 'blue';
												})()
											}
											style={{
												paddingLeft: '2px',
												paddingRight: '2px',
												marginRight: '2px',
												marginLeft: '2px',
												cursor: supportsVariableWhere ? 'pointer' : 'not-allowed',
												userSelect: 'none',
												opacity: supportsVariableWhere ? 1 : 0.45
											}}
											onClick={() => {
												if (supportsVariableWhere) {
													handleVariableWhereButtonClick(dataPoint.id);
												}
											}}
										>
											Filter ⚙
										</Tag>
									);
								})()}

								{(() => {
									const supportsFixedWhere = dataPointSupportsFixedWhere(dataPoint.dataType);

									return (
										<Tag
											color={(props.dataPointFixedWheres[dataPoint.id]?.where?.trim().length ?? 0) > 0 ? 'green' : 'blue'}
											style={{
												paddingLeft: '2px',
												paddingRight: '2px',
												marginRight: '2px',
												marginLeft: '2px',
												cursor: supportsFixedWhere ? 'pointer' : 'not-allowed',
												userSelect: 'none',
												opacity: supportsFixedWhere ? 1 : 0.45
											}}
											onClick={() => {
												if (supportsFixedWhere) {
													handleFixedWhereButtonClick(dataPoint.id);
												}
											}}
										>
											Where ⚙
										</Tag>
									);
								})()}

								{(() => {
									const supportsAggregate = dataPointSupportsAggregate(dataPoint.dataType);

									return (
										<Tag
											color={(props.dataPointAggregates[dataPoint.id]?.aggregate?.trim().length ?? 0) > 0 ? 'green' : 'blue'}
											style={{
												paddingLeft: '2px',
												paddingRight: '2px',
												marginRight: '2px',
												marginLeft: '2px',
												cursor: supportsAggregate ? 'pointer' : 'not-allowed',
												userSelect: 'none',
												opacity: supportsAggregate ? 1 : 0.45
											}}
											onClick={() => {
												if (supportsAggregate) {
													handleAggregateButtonClick(dataPoint.id);
												}
											}}
										>
											Aggreg. ⚙
										</Tag>
									);
								})()}

								<Tag
									color={props.selectedOrderByDataPointID === dataPoint.id ? 'green' : 'blue'}
									style={{ paddingLeft: '2px', paddingRight: '2px', marginRight: '2px', marginLeft: '2px', cursor: 'pointer', userSelect: 'none' }}
									onClick={() => handleOrderByTagClick(dataPoint.id)}
								>
									{props.selectedOrderByDataPointID === dataPoint.id ? 'Order By ✓' : <>Order By&nbsp;&nbsp;&nbsp;</>}
								</Tag>
							</span>
						</Row>
					</div>
				)}
			</Draggable>
		);
	});

	return (
		<>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={ahrbReportCoreDataPointCustomizationOpen}
                width='45%'
            >
				<AHRBCoreDataPointCustomization
					key={selectedCustomizationDataPointID}
					coreTypes = {props.selectedCoreTypes}
					coreDataPoints = {props.selectedCoreDataPoints}
					ahrbsDataPointID = {selectedCustomizationDataPointID}
					selectedCoreDataPointColumnCustomization = {selectedCustomizationDataPoint}
					onOk={(dataPointFormat: DataSetColumnFormatModel) => handleSetCoreDataPointCustomization(dataPointFormat)}
					onCancel={() => setAHRBReportCoreDataPointCustomizationOpen(false)}
				></AHRBCoreDataPointCustomization>
			</ServaModal>

            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={ahrbReportCoreDataPointFixedWhereOpen}
                width='45%'
            >
				<AHRBCoreDataPointFixedWhere
					key={selectedFixedWhereDataPointID}
					coreTypes = {props.selectedCoreTypes}
					coreDataPoints = {props.selectedCoreDataPoints}
					ahrbsDataPointID = {selectedFixedWhereDataPointID}
					selectedCoreDataPointColumnFixedWhere = {selectedFixedWhereDataPoint}
					onCancel={() => setAHRBReportCoreDataPointFixedWhereOpen(false)}
					onOk={(dataPointFixedWhere: AHRBDataPointFixedWhereConfiguration) => handleSetCoreDataPointFixedWhere(dataPointFixedWhere)}
				></AHRBCoreDataPointFixedWhere>
			</ServaModal>

			<ServaModal
				destroyOnHidden={true}
				hideButtons={true}
				open={ahrbReportCoreDataPointVariableWhereOpen}
				width='45%'
			>
				<AHRBCoreDataPointVariableWhere
					key={selectedVariableWhereDataPointID}
					coreTypes={props.selectedCoreTypes}
					coreDataPoints={props.selectedCoreDataPoints}
					ahrbsDataPointID={selectedVariableWhereDataPointID}
					selectedCoreDataPointColumnVariableWhere={selectedVariableWhereDataPoint}
					onCancel={() => setAHRBReportCoreDataPointVariableWhereOpen(false)}
					onOk={(dataPointVariableWhere: AdHocReportVariableWhereModel) => handleSetCoreDataPointVariableWhere(dataPointVariableWhere)}
				></AHRBCoreDataPointVariableWhere>
			</ServaModal>

            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={ahrbReportCoreDataPointAggregateOpen}
                width='45%'
            >
				<AHRBCoreDataPointAggregate
					key={selectedAggregateDataPointID}
					coreTypes = {props.selectedCoreTypes}
					coreDataPoints = {props.selectedCoreDataPoints}
					ahrbsDataPointID = {selectedAggregateDataPointID}
					selectedCoreDataPointColumnAggregate = {selectedAggregateDataPoint}
					onCancel={() => setAHRBReportCoreDataPointAggregateOpen(false)}
					onOk={(dataPointAggregate: AHRBDataPointAggregateConfiguration) => handleSetCoreDataPointAggregate(dataPointAggregate)}
				></AHRBCoreDataPointAggregate>
			</ServaModal>
			<div
				style={{ display: 'flex', flexFlow: 'column', height: '100%' }} 
				className='border-solid border-2 rounded-md h-full'
			>
				<h3 className='text-2xl p-2 mb-0'>Selected Fields</h3>
				<Text type='secondary'>Click and drag Field <i>left</i> to remove:</Text>
				<Droppable droppableId='dataPoints'>
					{(provided, snapshot) => (
						<Col
							span={24}
							ref={provided.innerRef}
							{...provided.droppableProps}
							style={{
								background: snapshot.isDraggingOver ? (props.coreTypes ? 'lightgreen' : 'lightblue') : 'white',
								padding: '1px',
								border: '1px solid gray',
								borderRadius: '5px',
								flex: '1 1 auto'
							}}
						>
							<div
								style={{
									boxSizing: 'border-box',
									height: '400px',
									overflowY: 'auto',
									padding: '8px'
								}}
							>
								{selectedDataPointRows}
							</div>
							{provided.placeholder}
						</Col>
					)}
				</Droppable>
			</div>
		</>
	);
};

export default AHRBSDataPoints;
